import { Sprite } from "./Sprite";
import { BitmapFont } from "./BitmapFont";
import { TextStyle } from "./css/TextStyle";
import { Event } from "../events/Event";
import { Point } from "../maths/Point";
import { Rectangle } from "../maths/Rectangle";
import { WordText } from "../utils/WordText";
import { ILaya } from "../../ILaya";
import { Config } from "../../Config";
import { Utils } from "../utils/Utils";
import { DrawRectCmd } from "./cmd/DrawRectCmd";
import { HtmlElement, HtmlElementType } from "../html/HtmlElement";
import { Pool } from "../utils/Pool";
import { HideFlags } from "../Const";
import { HtmlParser } from "../html/HtmlParser";
import { UBBParser } from "../html/UBBParser";
import { Browser } from "../utils/Browser";
import { SpriteConst, TransformKind } from "./SpriteConst";
import { TextRenderConfig } from "../webgl/text/TextRenderConfig";
export class Text extends Sprite {
    constructor() {
        super();
        this._overflow = Text.VISIBLE;
        this._singleCharRender = false;
        this._prompt = '';
        this._wordWrap = false;
        this._asPassword = false;
        this._textWidth = 0;
        this._textHeight = 0;
        this._html = false;
        this._ubb = false;
        this._maxWidth = 0;
        this._hideText = false;
        this._renderType |= SpriteConst.TEXT;
        this._textStyle = new TextStyle();
        this._textStyle.fontSize = Config.defaultFontSize;
        this._text = "";
        this.font = "";
        this._elements = [];
        this._lines = [];
        this._padding = [0, 0, 0, 0];
        this._fontSizeScale = 1;
        this.graphics._useSpriteRect = true;
    }
    static registerBitmapFont(name, bitmapFont) {
        bitmapFont._addReference();
        Text._bitmapFonts[name] = bitmapFont;
    }
    static unregisterBitmapFont(name, destroy = true) {
        let font = Text._bitmapFonts[name];
        if (font) {
            font._removeReference();
            if (destroy)
                font.destroy();
            delete Text._bitmapFonts[name];
        }
    }
    destroy(destroyChild = true) {
        recoverLines(this._lines);
        HtmlElement.returnToPool(this._elements);
        if (this._bgDrawCmd)
            this._bgDrawCmd.lock = false;
        super.destroy(destroyChild);
    }
    measureWidth() {
        this.typeset();
        return this._textWidth;
    }
    measureHeight() {
        this.typeset();
        return this._textHeight;
    }
    _transChanged(kind) {
        super._transChanged(kind);
        if ((kind & TransformKind.Size) != 0) {
            if (this._scrollRect != null) {
                this._updateScrollRect();
            }
            if (!this._updatingLayout)
                this.markChanged();
            else
                this.drawBg();
        }
    }
    _updateScrollRect() {
        let rect = this._scrollRect || new Rectangle();
        let rectWidth = this._isWidthSet ? this._width : this._textWidth;
        let rectHeight = this._isHeightSet ? this._height : this._textHeight;
        rect.setTo(0, 0, rectWidth, rectHeight);
        this.scrollRect = rect;
    }
    get textWidth() {
        this.typeset();
        return this._textWidth;
    }
    get textHeight() {
        this.typeset();
        return this._textHeight;
    }
    get text() {
        return this._text;
    }
    set text(value) {
        if (value == null)
            value = "";
        else if (typeof (value) !== "string")
            value = '' + value;
        if (!this.ignoreLang && Text.langPacks)
            value = Text.langPacks[value] || value;
        if (this._text != value) {
            this._text = value;
            this.markChanged();
            this.event(Event.CHANGE);
        }
    }
    changeText(text) {
        this.text = text;
    }
    get font() {
        return this._textStyle.font;
    }
    set font(value) {
        this._textStyle.font = value;
        if (!value) {
            value = Config.defaultFont;
            if (!value)
                value = "Arial";
        }
        this._realFont = value;
        this._bitmapFont = Text._bitmapFonts[value];
        if (this._bitmapFont) {
            if (this._text)
                this.markChanged();
        }
        else if (value && (Utils.getFileExtension(value) || value.startsWith("res://"))) {
            let t = value;
            let fontObj = ILaya.loader.getRes(value);
            if (!fontObj || fontObj.obsolute) {
                ILaya.loader.load(value).then(fontObj => {
                    if (this._realFont != t)
                        return;
                    if (!fontObj)
                        this._realFont = "arial";
                    else if (fontObj instanceof BitmapFont)
                        this._bitmapFont = fontObj;
                    else
                        this._realFont = fontObj.family;
                    if (this._text)
                        this.markChanged();
                });
            }
            else {
                if (fontObj instanceof BitmapFont)
                    this._bitmapFont = fontObj;
                else
                    this._realFont = fontObj.family;
                if (this._text)
                    this.markChanged();
            }
        }
        else {
            this._realFont = (Browser.onIPhone ? (Config.fontFamilyMap[value] || value) : value);
            if (this._text)
                this.markChanged();
        }
    }
    get realFont() {
        return this._realFont;
    }
    get fontSize() {
        return this._textStyle.fontSize;
    }
    set fontSize(value) {
        if (this._textStyle.fontSize != value) {
            this._textStyle.fontSize = value;
            this.markChanged();
        }
    }
    get color() {
        return this._textStyle.color;
    }
    set color(value) {
        if (this._textStyle.color != value) {
            this._textStyle.color = value;
            if (!this._isChanged && this._graphics && this._elements.length == 0)
                this._graphics.replaceTextColor(this._textStyle.color);
            else
                this.markChanged();
        }
    }
    get bold() {
        return this._textStyle.bold;
    }
    set bold(value) {
        if (this._textStyle.bold != value) {
            this._textStyle.bold = value;
            this.markChanged();
        }
    }
    get italic() {
        return this._textStyle.italic;
    }
    set italic(value) {
        if (this._textStyle.italic != value) {
            this._textStyle.italic = value;
            this.markChanged();
        }
    }
    get align() {
        return this._textStyle.align;
    }
    set align(value) {
        if (this._textStyle.align != value) {
            this._textStyle.align = value;
            this.markChanged();
        }
    }
    get valign() {
        return this._textStyle.valign;
    }
    set valign(value) {
        if (this._textStyle.valign != value) {
            this._textStyle.valign = value;
            this.markChanged();
        }
    }
    get alignItems() {
        return this._textStyle.alignItems;
    }
    set alignItems(value) {
        if (this._textStyle.alignItems != value) {
            this._textStyle.alignItems = value;
            this.markChanged();
        }
    }
    get wordWrap() {
        return this._wordWrap;
    }
    set wordWrap(value) {
        if (this._wordWrap != value) {
            this._wordWrap = value;
            this.markChanged();
        }
    }
    get leading() {
        return this._textStyle.leading;
    }
    set leading(value) {
        if (this._textStyle.leading != value) {
            this._textStyle.leading = value;
            this.markChanged();
        }
    }
    get padding() {
        return this._padding;
    }
    set padding(value) {
        if (typeof (value) == 'string') {
            let arr = value.split(",");
            this._padding.length = 0;
            for (let i = 0; i < 4; i++) {
                let v = parseFloat(arr[i]);
                if (isNaN(v))
                    v = 0;
                this._padding.push(v);
            }
        }
        else
            this._padding = value;
        this.markChanged();
    }
    get bgColor() {
        return this._bgColor;
    }
    set bgColor(value) {
        this._bgColor = value;
        this.drawBg();
    }
    get borderColor() {
        return this._borderColor;
    }
    set borderColor(value) {
        this._borderColor = value;
        this.drawBg();
    }
    get stroke() {
        return this._textStyle.stroke;
    }
    set stroke(value) {
        if (this._textStyle.stroke != value) {
            this._textStyle.stroke = value;
            this.markChanged();
        }
    }
    get strokeColor() {
        return this._textStyle.strokeColor;
    }
    set strokeColor(value) {
        if (this._textStyle.strokeColor != value) {
            this._textStyle.strokeColor = value;
            this.markChanged();
        }
    }
    get overflow() {
        return this._overflow;
    }
    set overflow(value) {
        if (this._overflow != value) {
            this._overflow = value;
            if (value !== Text.VISIBLE) {
                this._updateScrollRect();
            }
            else
                this.scrollRect = null;
        }
    }
    get underline() {
        return this._textStyle.underline;
    }
    set underline(value) {
        if (this._textStyle.underline != value) {
            this._textStyle.underline = value;
            this.markChanged();
        }
    }
    get underlineColor() {
        return this._textStyle.underlineColor;
    }
    set underlineColor(value) {
        if (this._textStyle.underlineColor != value) {
            this._textStyle.underlineColor = value;
            this.markChanged();
        }
    }
    get strikethrough() {
        return this._textStyle.strikethrough;
    }
    set strikethrough(value) {
        if (this._textStyle.strikethrough != value) {
            this._textStyle.strikethrough = value;
            this.markChanged();
        }
    }
    get strikethroughColor() {
        return this._textStyle.strikethroughColor;
    }
    set strikethroughColor(value) {
        if (this._textStyle.strikethroughColor != value) {
            this._textStyle.strikethroughColor = value;
            this.markChanged();
        }
    }
    get singleCharRender() {
        return this._singleCharRender;
    }
    set singleCharRender(value) {
        if (this._singleCharRender !== value) {
            this._singleCharRender = value;
            this.markChanged();
        }
    }
    get html() {
        return this._html;
    }
    set html(value) {
        if (this._html != value) {
            this._html = value;
            this.markChanged();
        }
    }
    get ubb() {
        return this._ubb;
    }
    set ubb(value) {
        if (this._ubb != value) {
            this._ubb = value;
            this.markChanged();
        }
    }
    get maxWidth() {
        return this._maxWidth;
    }
    set maxWidth(value) {
        if (this._maxWidth != value) {
            this._maxWidth = value;
            this.markChanged();
        }
    }
    get htmlParseOptions() {
        return this._htmlParseOptions;
    }
    set htmlParseOptions(value) {
        this._htmlParseOptions = value;
    }
    get templateVars() {
        return this._templateVars;
    }
    set templateVars(value) {
        if (!this._templateVars && !value)
            return;
        if (value === true)
            this._templateVars = {};
        else if (value === false)
            this._templateVars = null;
        else
            this._templateVars = value;
        this.markChanged();
    }
    setVar(name, value) {
        if (!this._templateVars)
            this._templateVars = {};
        this._templateVars[name] = value;
        this.markChanged();
        return this;
    }
    get scrollX() {
        if (!this._scrollPos)
            return 0;
        return this._scrollPos.x;
    }
    set scrollX(value) {
        this.typeset();
        if (!this._scrollPos)
            return;
        let maxScrollX = this.maxScrollX;
        value = value < 0 ? 0 : value;
        value = value > maxScrollX ? maxScrollX : value;
        this._scrollPos.x = value;
        this.renderText();
    }
    get scrollY() {
        if (!this._scrollPos)
            return 0;
        return this._scrollPos.y;
    }
    set scrollY(value) {
        this.typeset();
        if (!this._scrollPos)
            return;
        let maxScrollY = this.maxScrollY;
        value = value < 0 ? 0 : value;
        value = value > maxScrollY ? maxScrollY : value;
        this._scrollPos.y = value;
        this.renderText();
    }
    get maxScrollX() {
        let r = this.textWidth - this._width;
        return r < 0 ? 0 : r;
    }
    get maxScrollY() {
        let r = this.textHeight - this._height;
        return r < 0 ? 0 : r;
    }
    get lines() {
        this.typeset();
        return this._lines;
    }
    markChanged() {
        if (!this._isChanged) {
            this._isChanged = true;
            ILaya.systemTimer.callLater(this, this._typeset);
        }
    }
    typeset() {
        this._isChanged && ILaya.systemTimer.runCallLater(this, this._typeset);
    }
    refreshLayout() {
        ILaya.systemTimer.callLater(this, this.doLayout);
    }
    get objContainer() {
        if (!this._objContainer) {
            this._objContainer = new Sprite();
            this._objContainer.hideFlags |= HideFlags.HideAndDontSave;
            this.addChild(this._objContainer);
        }
        return this._objContainer;
    }
    hideText(value) {
        this._hideText = value;
        if (value) {
            this.graphics.clear(true, this._bgDrawCmd);
        }
        else {
            this.markChanged();
            this.typeset();
        }
    }
    _typeset() {
        this._isChanged = false;
        if (this._hideText || this._destroyed)
            return;
        HtmlElement.returnToPool(this._elements);
        if (this._objContainer)
            this._objContainer.removeChildren();
        let text = this._text;
        if (this._onTranslate)
            text = this._onTranslate(text, this._templateVars);
        let isPrompt;
        if (!text && this._prompt) {
            text = this._prompt;
            isPrompt = true;
        }
        if (!text) {
            this.graphics.clear(true, this._bgDrawCmd);
            this._textWidth = this._textHeight = 0;
            this._scrollPos = null;
            if (this._onPostLayout) {
                this._updatingLayout = true;
                this._onPostLayout();
                this._updatingLayout = false;
            }
            return;
        }
        let html = this._html;
        text = text.replace(normalizeCR, "\n");
        if (this._parseEscapeChars)
            text = text.replace(escapeCharsPattern, getReplaceStr);
        if (!isPrompt && this._templateVars)
            text = Utils.parseTemplate(text, this._templateVars);
        if (this._ubb) {
            text = UBBParser.defaultParser.parse(text);
            html = true;
        }
        if (!isPrompt && this._asPassword)
            text = Text._passwordChar.repeat(text.length);
        let saveColor;
        if (isPrompt) {
            saveColor = this._textStyle.color;
            this._textStyle.color = this._promptColor;
        }
        if (html)
            HtmlParser.defaultParser.parse(text, this._textStyle, this._elements, this._htmlParseOptions);
        else {
            let ele = HtmlElement.getFromPool(HtmlElementType.Text);
            Object.assign(ele.style, this._textStyle);
            ele.text = text;
            this._elements.push(ele);
        }
        if (isPrompt)
            this._textStyle.color = saveColor;
        this.doLayout();
    }
    doLayout() {
        if (this._destroyed)
            return;
        this._updatingLayout = true;
        this._fontSizeScale = 1;
        let wordWrap = this._wordWrap || this._overflow == Text.ELLIPSIS;
        let noBreakWord = this._wordWrap;
        let padding = this._padding;
        let rectWidth;
        if (this._isWidthSet)
            rectWidth = this._width - padding[3] - padding[1];
        else
            rectWidth = Number.MAX_VALUE;
        if (this._maxWidth > 0) {
            let m = this._maxWidth - padding[3] - padding[1];
            if (!wordWrap || m < rectWidth)
                rectWidth = m;
            wordWrap = true;
        }
        let rectHeight = this._isHeightSet ? (this._height - padding[0] - padding[2]) : Number.MAX_VALUE;
        let bfont = this._bitmapFont;
        let alignItems = this._textStyle.alignItems == "middle" ? 1 : (this._textStyle.alignItems == "bottom" ? 2 : 0);
        let lineX, lineY;
        let curLine;
        let lastCmd;
        let charWidth, charHeight;
        let fontSize;
        let ctxFont;
        let getTextWidth = (text) => {
            if (bfont)
                return bfont.getTextWidth(text, fontSize);
            else {
                let ret = Browser.context.measureText(text);
                return ret ? ret.width : 100;
            }
        };
        let getTextWidth2 = (text, font, fontSize) => {
            if (bfont) {
                return bfont.getTextWidth(text, fontSize);
            }
            else {
                let t = Browser.context.font;
                Browser.context.font = font;
                let ret = Browser.context.measureText(text);
                Browser.context.font = t;
                return ret ? ret.width : 100;
            }
        };
        let buildLines = (text, style) => {
            fontSize = Math.floor(style.fontSize * this._fontSizeScale);
            if (fontSize == 0)
                fontSize = 1;
            if (bfont) {
                charWidth = bfont.getMaxWidth(fontSize);
                charHeight = bfont.getMaxHeight(fontSize);
            }
            else {
                Browser.context.font = ctxFont = (style.italic ? "italic " : "") + (style.bold ? "bold " : "") + fontSize + "px " + this._realFont;
                let mr = Browser.context.measureText(Text._testWord);
                if (mr) {
                    charWidth = mr.width;
                    charHeight = Math.ceil(mr.height || fontSize);
                }
                else {
                    charWidth = 100;
                    charHeight = fontSize;
                }
            }
            let lines = text.split("\n");
            if (wordWrap) {
                for (let i = 0, n = lines.length; i < n; i++) {
                    let line = lines[i];
                    if (line.length > 0)
                        wrapText(line, style);
                    if (i != n - 1) {
                        addLine();
                    }
                }
            }
            else {
                for (let i = 0, n = lines.length; i < n; i++) {
                    let line = lines[i];
                    if (line.length > 0)
                        addCmd(line, style, null);
                    if (i != n - 1) {
                        addLine();
                    }
                }
            }
        };
        let addCmd = (target, style, width) => {
            let cmd = cmdPool.length > 0 ? cmdPool.pop() : {};
            cmd.x = lineX;
            cmd.y = lineY;
            if (typeof (target) === "string") {
                if (!width)
                    width = getTextWidth(target);
                if (!cmd.wt)
                    cmd.wt = new WordText();
                cmd.wt.setText(target);
                cmd.wt.width = width;
                cmd.wt.splitRender = this._singleCharRender;
                cmd.ctxFont = ctxFont;
                cmd.fontSize = fontSize;
                cmd.width = width;
                cmd.height = charHeight;
            }
            else {
                cmd.obj = target;
                cmd.width = target.width;
                cmd.height = target.height;
                if (target.width > 0) {
                    cmd.x++;
                    cmd.width += 2;
                }
            }
            cmd.style = style;
            cmd.linkEnd = false;
            cmd.next = null;
            cmd.prev = lastCmd;
            lineX += Math.round(cmd.width);
            if (lastCmd)
                lastCmd.next = cmd;
            else
                curLine.cmd = cmd;
            lastCmd = cmd;
        };
        let moveCmds = (cmd) => {
            while (cmd.linkEnd && cmd.next) {
                cmd = cmd.next;
            }
            if (!cmd)
                return;
            cmd.prev.next = null;
            while (cmd) {
                let next = cmd.next;
                cmd.x = lineX;
                cmd.y = lineY;
                cmd.next = null;
                cmd.prev = lastCmd;
                lineX += Math.round(cmd.width);
                if (lastCmd)
                    lastCmd.next = cmd;
                else
                    curLine.cmd = cmd;
                lastCmd = cmd;
                cmd = next;
            }
        };
        let splitCmd = (cmd, pos) => {
            let ccode = cmd.wt.text.charCodeAt(pos);
            if (isLowSurrogate(ccode))
                pos--;
            if (pos == 0)
                return false;
            let str = cmd.wt.text.substring(pos);
            cmd.wt.setText(cmd.wt.text.substring(0, pos));
            cmd.width = cmd.wt.width = getTextWidth2(cmd.wt.text, cmd.ctxFont, cmd.fontSize);
            let cmd2 = cmdPool.length > 0 ? cmdPool.pop() : {};
            if (!cmd2.wt)
                cmd2.wt = new WordText();
            cmd2.wt.setText(str);
            cmd2.style = cmd.style;
            cmd2.ctxFont = cmd.ctxFont;
            cmd2.fontSize = cmd.fontSize;
            cmd2.width = cmd2.wt.width = getTextWidth2(str, cmd.ctxFont, cmd.fontSize);
            cmd2.height = cmd.height;
            cmd2.next = cmd.next;
            cmd2.prev = cmd;
            cmd.next = cmd2;
            return true;
        };
        let addLine = (last) => {
            lineX = 0;
            if (curLine) {
                let lineHeight = 0;
                let lineWidth = 0;
                let cmd = curLine.cmd;
                while (cmd) {
                    if (cmd.height > lineHeight)
                        lineHeight = cmd.height;
                    lineWidth += cmd.width;
                    cmd = cmd.next;
                }
                cmd = curLine.cmd;
                while (cmd) {
                    if (alignItems == 1)
                        cmd.y = Math.floor((lineHeight - cmd.height) * 0.5);
                    else if (alignItems == 2)
                        cmd.y = Math.floor((lineHeight - cmd.height));
                    else
                        cmd.y = 0;
                    cmd = cmd.next;
                }
                if (lineHeight == 0)
                    lineHeight = charHeight;
                lineHeight++;
                curLine.height = lineHeight;
                curLine.width = Math.round(lineWidth);
                lineY += curLine.height + Math.floor(this._textStyle.leading * this._fontSizeScale);
            }
            if (last)
                return null;
            curLine = linePool.length > 0 ? linePool.pop() : {};
            curLine.x = 0;
            curLine.y = lineY;
            this._lines.push(curLine);
            lastCmd = null;
            return curLine;
        };
        let wrapText = (text, style) => {
            let remainWidth = Math.max(0, rectWidth - lineX);
            let tw = getTextWidth(text);
            if (tw <= remainWidth) {
                addCmd(text, style, tw);
                return;
            }
            let maybeIndex = 0;
            let wordWidth = 0;
            let startIndex = 0;
            let isPunc;
            let testResult;
            let isEmoji = emojiTest.test(text);
            if (!bfont && !isEmoji) {
                maybeIndex = Math.floor(remainWidth / charWidth);
                if (maybeIndex != 0)
                    wordWidth = getTextWidth(text.substring(0, maybeIndex));
            }
            let len = text.length;
            for (let j = maybeIndex; j < len; j++) {
                let cc = text.charAt(j);
                let ccode = cc.charCodeAt(0);
                if (isEmoji && isHighSurrogate(ccode) && j + 1 < len)
                    cc += text.charAt(j + 1);
                tw = getTextWidth(cc);
                wordWidth += tw;
                if (wordWidth <= remainWidth || j === startIndex && lineX === 0) {
                    if (cc.length > 1)
                        j++;
                    continue;
                }
                let part = text.substring(startIndex, j);
                wordWidth -= tw;
                if (noBreakWord && ((ccode >= 65 && ccode <= 90) || (ccode >= 97 && ccode <= 122)
                    || (ccode >= 48 && ccode <= 57)
                    || (isPunc = punctuationChars.includes(ccode)))) {
                    let wb = part.length > 0 ? ((testResult = wordBoundaryTest.exec(part)) ? testResult.index : null) : 0;
                    if (wb > 0) {
                        if (wb > part.length - maxWordLength) {
                            j = startIndex + wb;
                            part = text.substring(startIndex, j);
                            wordWidth = null;
                            tw = null;
                        }
                    }
                    else if (wb != null && lastCmd != null) {
                        let cmd = lastCmd;
                        let totalLen = part.length;
                        let newLine = false;
                        while (cmd) {
                            if (cmd.width > 0) {
                                if (cmd.obj != null)
                                    break;
                                testResult = wordBoundaryTest.exec(cmd.wt.text);
                                let textLen = cmd.wt.text.length;
                                if (testResult == null) {
                                    addLine();
                                    if (isPunc && totalLen == 0) {
                                        if (splitCmd(cmd, textLen - 1))
                                            moveCmds(cmd.next);
                                        else if (cmd.x > 0)
                                            moveCmds(cmd);
                                    }
                                    else if (cmd.next != null)
                                        moveCmds(cmd.next);
                                    newLine = true;
                                    break;
                                }
                                else if (testResult.index > 0) {
                                    if (testResult.index > textLen - (maxWordLength - totalLen)) {
                                        addLine();
                                        splitCmd(cmd, testResult.index);
                                        moveCmds(cmd.next);
                                        newLine = true;
                                    }
                                    break;
                                }
                                else {
                                    totalLen += textLen;
                                    if (totalLen >= maxWordLength)
                                        break;
                                }
                            }
                            cmd = cmd.prev;
                        }
                        if (newLine) {
                            remainWidth = rectWidth - lineX;
                            if (wordWidth + tw < remainWidth) {
                                wordWidth += tw;
                                continue;
                            }
                        }
                    }
                    else {
                        if (isPunc) {
                            let b = (isEmoji && j >= 1 && isLowSurrogate(text.charCodeAt(j - 1))) ? 2 : 1;
                            if (j - b > startIndex || lineX > 0) {
                                j -= b;
                                part = text.substring(startIndex, j);
                                wordWidth = null;
                                tw = null;
                            }
                        }
                    }
                }
                if (part.length > 0)
                    addCmd(part, style, wordWidth);
                addLine();
                startIndex = j;
                remainWidth = rectWidth;
                wordWidth = null;
                if (maybeIndex > 1)
                    j += maybeIndex - 1;
                else if (tw != null) {
                    wordWidth = tw;
                    if (cc.length > 1)
                        j++;
                }
                else if (isEmoji && isHighSurrogate(text.charCodeAt(j)))
                    j++;
                if (wordWidth == null && j < len - 1)
                    wordWidth = getTextWidth(text.substring(startIndex, j + 1));
            }
            addCmd(text.substring(startIndex, len), style);
        };
        let calcTextSize = () => {
            let nw = 0, nh = 0;
            for (let line of this._lines) {
                if (line.width > nw)
                    nw = line.width;
            }
            if (nw > 0)
                nw += padding[1] + padding[3];
            this._textWidth = nw;
            let lastLine = this._lines[this._lines.length - 1];
            if (lastLine)
                nh = lastLine.y + lastLine.height;
            if (nh > 0)
                nh += padding[0] + padding[2];
            this._textHeight = nh;
        };
        let run = () => {
            lineX = lineY = charWidth = charHeight = 0;
            curLine = null;
            lastCmd = null;
            recoverLines(this._lines);
            addLine();
            let elements = this._elements;
            for (let i = 0, n = elements.length; i < n; i++) {
                let ele = elements[i];
                if (ele.type == HtmlElementType.Text) {
                    buildLines(ele.text, ele.style);
                }
                else if (ele.type == HtmlElementType.LinkEnd) {
                    if (lastCmd)
                        lastCmd.linkEnd = true;
                }
                else {
                    let htmlObj = ele.obj;
                    if (!htmlObj) {
                        let cls = HtmlParser.classMap[ele.type];
                        if (cls) {
                            htmlObj = Pool.createByClass(cls);
                            htmlObj.create(this, ele);
                            ele.obj = htmlObj;
                        }
                    }
                    if (htmlObj) {
                        if (wordWrap) {
                            let remainWidth = rectWidth - lineX;
                            if (htmlObj.width > 0 && remainWidth < htmlObj.width + 1) {
                                if (lineX > 0) {
                                    addLine();
                                }
                            }
                        }
                        addCmd(htmlObj, ele.style);
                    }
                }
            }
            addLine(true);
            calcTextSize();
        };
        run();
        if (this._overflow == Text.SHRINK) {
            if (this._lines.length > 1 && this._textHeight > rectHeight) {
                let low = 0;
                let high = this._textStyle.fontSize;
                this._fontSizeScale = Math.sqrt(rectHeight / this._textHeight);
                let cur = Math.floor(this._fontSizeScale * this._textStyle.fontSize);
                while (true) {
                    run();
                    if (this._textWidth > rectWidth || this._textHeight > rectHeight)
                        high = cur;
                    else
                        low = cur;
                    if (high - low > 1 || high != low && cur == high) {
                        cur = low + (high - low) / 2;
                        this._fontSizeScale = cur / this._textStyle.fontSize;
                    }
                    else
                        break;
                }
            }
            else if (this._textWidth > rectWidth) {
                this._fontSizeScale = rectWidth / this._textWidth;
                run();
                if (this._textWidth > rectWidth) {
                    let size = Math.floor(this._textStyle.fontSize * this._fontSizeScale);
                    size--;
                    this._fontSizeScale = size / this._textStyle.fontSize;
                    run();
                }
            }
        }
        else if (this._overflow == Text.ELLIPSIS
            && (this._textWidth > rectWidth || this._textHeight > rectHeight || !this._wordWrap && this._lines.length > 1)) {
            let i;
            if (!this._wordWrap && this._lines.length > 1)
                i = 1;
            else {
                i = this._lines.findIndex(line => line.y + line.height > rectHeight);
                if (i === 0)
                    i = 1;
            }
            let linesDeleted = false;
            if (i != -1 && this._lines.length > i) {
                recoverLines(this._lines.splice(i, this._lines.length - i), true);
                linesDeleted = true;
            }
            curLine = this._lines[this._lines.length - 1];
            let cmd = curLine.cmd;
            let next;
            let textCmd;
            let done = false;
            while (cmd) {
                next = cmd.next;
                if (!cmd.obj)
                    textCmd = cmd;
                if (done) {
                    cleanCmd(cmd, true);
                    cmdPool.push(cmd);
                }
                else if ((!next && linesDeleted) || cmd.x + cmd.width > rectWidth - 10) {
                    if (cmd.obj) {
                        cleanCmd(cmd, true);
                        cmd.wt = new WordText();
                        cmd.wt.setText(ellipsisStr);
                        if (textCmd) {
                            cmd.ctxFont = textCmd.ctxFont;
                            cmd.fontSize = textCmd.fontSize;
                            cmd.height = textCmd.height;
                            cmd.style = textCmd.style;
                        }
                        else {
                            cmd.ctxFont = ctxFont;
                            cmd.fontSize = fontSize;
                            cmd.height = charHeight;
                            cmd.style = this._textStyle;
                        }
                        cmd.wt.splitRender = this._singleCharRender;
                    }
                    else {
                        let space = cmd.x + cmd.width - rectWidth;
                        let remove = space < 5 ? 2 : space < 10 ? 1 : 0;
                        let min = cmd === curLine.cmd ? 1 : 0;
                        let i = cmd.wt.text.length;
                        while (i > min && remove > 0) {
                            if (isLowSurrogate(cmd.wt.text.charCodeAt(i - 1)))
                                i--;
                            i--;
                            remove--;
                        }
                        cmd.wt.setText(cmd.wt.text.substring(0, i) + ellipsisStr);
                    }
                    cmd.width = cmd.wt.width = getTextWidth2(cmd.wt.text, cmd.ctxFont, cmd.fontSize);
                    cmd.next = null;
                    done = true;
                    addLine(true);
                }
                cmd = next;
            }
            if (done || linesDeleted)
                calcTextSize();
        }
        if (this._onPostLayout)
            this._onPostLayout();
        let align = this._textStyle.align == "center" ? 1 : (this._textStyle.align == "right" ? 2 : 0);
        if (align != 0 && this._isWidthSet) {
            let rectWidth = this._width - padding[3] - padding[1];
            for (let line of this._lines) {
                let offsetX = 0;
                if (align == 1)
                    offsetX = Math.floor((rectWidth - line.width) * 0.5);
                else if (align == 2)
                    offsetX = rectWidth - line.width;
                if (offsetX > 0)
                    line.x = offsetX;
            }
        }
        if (this._isHeightSet && this._textHeight < this._height) {
            let offsetY = 0;
            if (this._textStyle.valign === "middle")
                offsetY = Math.floor((this._height - this._textHeight) * 0.5);
            else if (this._textStyle.valign === "bottom")
                offsetY = this._height - this._textHeight;
            if (offsetY > 0) {
                for (let line of this._lines) {
                    line.y += offsetY;
                }
            }
        }
        if (this._overflow == Text.SCROLL
            && (this._isWidthSet && this._textWidth > this._width || this._isHeightSet && this._textHeight > this._height)) {
            if (!this._scrollPos)
                this._scrollPos = new Point(0, 0);
            else {
                let maxScrollX = this.maxScrollX;
                let maxScrollY = this.maxScrollY;
                if (this._scrollPos.x > maxScrollX)
                    this._scrollPos.x = maxScrollX;
                if (this._scrollPos.y > maxScrollY)
                    this._scrollPos.y = maxScrollY;
            }
        }
        else
            this._scrollPos = null;
        if (this._objContainer) {
            this._objContainer.size(this._width, this._height);
            if (this._scrollPos || this._overflow == Text.HIDDEN && this._objContainer.numChildren > 0) {
                let rect = this._objContainer.scrollRect || new Rectangle();
                let rectWidth = this._isWidthSet ? this._width : this._textWidth;
                let rectHeight = this._isHeightSet ? this._height : this._textHeight;
                this._objContainer.scrollRect = rect.setTo(0, 0, rectWidth, rectHeight);
            }
            else
                this._objContainer.scrollRect = null;
        }
        this._updatingLayout = false;
        this.renderText();
    }
    renderText() {
        let graphics = this.graphics;
        graphics.clear(true, this._bgDrawCmd);
        this._fontGlobalScale = TextRenderConfig.fontScale;
        let padding = this._padding;
        let paddingLeft = padding[3];
        let paddingTop = padding[0];
        let bfont = this._bitmapFont;
        let scrollPos = this._scrollPos;
        let rectWidth = this._isWidthSet ? this._width : this._textWidth;
        let rectHeight = this._isHeightSet ? this._height : this._textHeight;
        let bottom = rectHeight - padding[2];
        let clipped = this._overflow == Text.HIDDEN || this._overflow == Text.SCROLL;
        rectWidth -= (padding[3] + padding[1]);
        rectHeight -= (padding[0] + padding[2]);
        let x = 0, y = 0;
        let lines = this._lines;
        let lineCnt = lines.length;
        let curLink;
        let linkStartX;
        for (let i = 0; i < lineCnt; i++) {
            let line = lines[i];
            x = paddingLeft + line.x;
            y = paddingTop + line.y;
            if (scrollPos) {
                x -= scrollPos.x;
                y -= scrollPos.y;
            }
            let lineClipped = clipped && ((y + line.height) <= paddingTop || y >= bottom);
            let cmd = line.cmd;
            while (cmd) {
                if (cmd.linkEnd) {
                    if (curLink) {
                        curLink.addRect(linkStartX, y, x + cmd.x + cmd.width - linkStartX, line.height);
                        curLink = null;
                    }
                }
                if (cmd.obj) {
                    cmd.obj.pos(x + cmd.x, y + cmd.y);
                    if (cmd.obj.element.type == HtmlElementType.Link) {
                        curLink = cmd.obj;
                        curLink.resetArea();
                        linkStartX = x + cmd.x;
                    }
                }
                else if (!lineClipped) {
                    if (bfont) {
                        let tx = 0;
                        let str = cmd.wt.text;
                        let color = bfont.tint ? cmd.style.color : "#FFFFFF";
                        let scale = Math.floor((bfont.autoScaleSize ? cmd.style.fontSize : bfont.fontSize) * this._fontSizeScale) / bfont.fontSize;
                        for (let i = 0, n = str.length; i < n; i++) {
                            let c = str.charCodeAt(i);
                            let g = bfont.dict[c];
                            if (g) {
                                if (g.texture)
                                    graphics.drawImage(g.texture, x + cmd.x + tx + g.x * scale, y + cmd.y + g.y * scale, g.width * scale, g.height * scale, color);
                                tx += Math.round(g.advance * scale);
                            }
                        }
                    }
                    else {
                        if (cmd.style.stroke)
                            graphics.fillBorderText(cmd.wt, x + cmd.x, y + cmd.y, cmd.ctxFont, cmd.style.color, null, cmd.style.stroke, cmd.style.strokeColor);
                        else
                            graphics.fillText(cmd.wt, x + cmd.x, y + cmd.y, cmd.ctxFont, cmd.style.color, null);
                    }
                }
                if (!lineClipped && cmd.width > 0) {
                    if (cmd.style.underline) {
                        let thickness = Math.max(1, cmd.fontSize / 16);
                        graphics.drawLine(x + cmd.x, y + line.height - thickness, x + cmd.x + cmd.width, y + line.height - thickness, cmd.style.underlineColor || cmd.style.color, thickness);
                    }
                    if (cmd.style.strikethrough) {
                        let thickness = Math.max(1, cmd.fontSize / 16);
                        let stx = x + cmd.x;
                        let sty = (y + line.height / 2 - thickness) | 0;
                        let ext = 4;
                        graphics.drawLine(stx - ext, sty, stx + cmd.width + ext, sty, cmd.style.strikethroughColor || cmd.style.color, thickness);
                    }
                }
                cmd = cmd.next;
            }
            if (curLink) {
                curLink.addRect(linkStartX, y, rectWidth - linkStartX + paddingLeft, line.height);
                linkStartX = paddingLeft;
            }
        }
        if (clipped) {
            this._updateScrollRect();
        }
    }
    drawBg() {
        let cmd = this._bgDrawCmd;
        if (this._bgColor || this._borderColor) {
            if (!cmd) {
                cmd = new DrawRectCmd();
                cmd.x = cmd.y = 0;
                cmd.width = cmd.height = 1;
                cmd.percent = true;
                cmd.lock = true;
                this._bgDrawCmd = cmd;
            }
            cmd.fillColor = this._bgColor;
            cmd.lineColor = this._borderColor;
            cmd.lineWidth = this._borderColor ? 1 : 0;
            let cmds = this.graphics.cmds;
            let i = cmds.indexOf(cmd);
            if (i != 0) {
                if (i != -1)
                    cmds.splice(i, 1);
                cmds.unshift(cmd);
                this.graphics.cmds = cmds;
            }
        }
        else if (cmd) {
            this.graphics.removeCmd(cmd, true);
            this._bgDrawCmd = null;
        }
    }
    _setParent(value) {
        super._setParent(value);
        if (value && this._fontGlobalScale != null && this._fontGlobalScale !== TextRenderConfig.fontScale) {
            this.repaint();
        }
    }
}
Text.VISIBLE = "visible";
Text.SCROLL = "scroll";
Text.HIDDEN = "hidden";
Text.SHRINK = "shrink";
Text.ELLIPSIS = "ellipsis";
Text.RightToLeft = false;
Text._testWord = "游";
Text._passwordChar = "●";
Text._bitmapFonts = {};
const cmdPool = [];
const linePool = [];
function recoverLines(lines, releaseObjs) {
    for (let line of lines) {
        let cmd = line.cmd;
        while (cmd) {
            cleanCmd(cmd, releaseObjs);
            cmdPool.push(cmd);
            cmd = cmd.next;
        }
        line.cmd = null;
    }
    linePool.push(...lines);
    lines.length = 0;
}
function cleanCmd(cmd, releaseObj) {
    if (cmd.obj) {
        if (releaseObj) {
            cmd.obj.element.obj = null;
            cmd.obj.release();
            Pool.recoverByClass(cmd.obj);
        }
        cmd.obj = null;
    }
    else if (cmd.wt)
        cmd.wt.cleanCache();
}
const emojiTest = /[\uD800-\uDBFF][\uDC00-\uDFFF]/;
const wordBoundaryTest = /[a-zA-Z0-9\!-\+\/_]+$/;
const punctuationChars = Array.from(".,，。、!！；;”’)）]】}》").map(char => char.charCodeAt(0));
const normalizeCR = /\r\n/g;
const escapeCharsPattern = /\\(\w)/g;
const escapeSequence = { "\\n": "\n", "\\t": "\t" };
const ellipsisStr = "…";
const maxWordLength = 20;
function getReplaceStr(word) {
    return escapeSequence[word];
}
function isHighSurrogate(c) {
    return c >= 0xD800 && c <= 0xDBFF;
}
function isLowSurrogate(c) {
    return c >= 0xDC00 && c <= 0xDFFF;
}
